import { useEffect, useState } from "react";
import { Button, Form, Icon, Label, Message, Modal, Segment, Table } from "semantic-ui-react";
import { billApi } from "../../../api/api";
import { mapBillPrint, mapBillRevision } from "../../../api/mappers";
import { printHtml, viewHtml } from "../../../utils/printHtml";
import { formatRupees } from "../../../utils/money";
import { fmtDateTime } from "../adminHelpers";
import { billHtml } from "./billPrint";
import { MODE_OPTIONS } from "./BillPayments";

/* Paid / Not paid for a final bill, from the payment summary. Null while the
   bill is a draft — there is nothing to be paid against yet. */
export function paidState(bill, summary) {
  if (!bill || bill.status !== "FINALIZED" || !summary) return null;
  if (Number(summary.refundDue) > 0) return { key: "refund", label: `Refund due ${formatRupees(summary.refundDue)}`, color: "red" };
  if (summary.settled) return { key: "paid", label: "Paid", color: "green" };
  return { key: "unpaid", label: `Not paid — ${formatRupees(summary.balanceDue)} due`, color: "orange" };
}

export function PaidLabel({ bill, summary, size }) {
  const state = paidState(bill, summary);
  if (!state) return null;
  return (
    <Label color={state.color} size={size}>
      <Icon name={state.key === "paid" ? "check circle" : "rupee sign"} />{state.label}
    </Label>
  );
}

/* "Bill paid": records the whole balance as one settlement — after which the
   bill reads Paid. Asks only how it was paid. */
export function MarkPaidButton({ summary, busy, onRecord }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("CASH");
  const [receipt, setReceipt] = useState("");
  const [reference, setReference] = useState("");
  const due = summary?.balanceDue;
  if (!(Number(due) > 0)) return null;

  const confirm = async () => {
    const ok = await onRecord({
      kind: "SETTLEMENT", mode, amount: String(Number(due)),
      receipt_number: receipt.trim(), reference: reference.trim(),
    });
    if (ok) setOpen(false);
  };

  return (
    <>
      <Button color="green" disabled={busy} onClick={() => setOpen(true)}>
        <Icon name="check circle" />Bill paid
      </Button>
      {open && (
        <Modal open size="tiny" onClose={() => setOpen(false)}>
          <Modal.Header>Mark the bill as paid?</Modal.Header>
          <Modal.Content>
            <p>This records <strong>{formatRupees(due)}</strong> received as the final settlement, and the bill will show as Paid.</p>
            <Form>
              <Form.Select id="paid-mode" label="Paid by" options={MODE_OPTIONS} value={mode}
                onChange={(_, d) => setMode(d.value)} />
              <Form.Group widths="equal">
                <Form.Input id="paid-receipt" label="Receipt slip no. (optional)" value={receipt}
                  onChange={(_, d) => setReceipt(d.value)} />
                <Form.Input id="paid-ref" label="UTR / reference (optional)" value={reference}
                  onChange={(_, d) => setReference(d.value)} />
              </Form.Group>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setOpen(false)}>Not yet</Button>
            <Button positive loading={busy} disabled={busy} onClick={confirm}>
              Yes, {formatRupees(due)} received
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
}

const STATUS_TEXT = { DRAFT: "Draft", FINALIZED: "Final", CANCELLED: "Cancelled" };

/* Earlier revisions of this booking's bill (the ones a "Correct" or a
   cancel replaced), each viewable and printable as it was issued. */
export function BillRevisions({ bookingId, currentBillId }) {
  const [revisions, setRevisions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    billApi.revisions(bookingId)
      .then((data) => live && setRevisions(data.map(mapBillRevision)))
      .catch(() => live && setRevisions([]));
    return () => { live = false; };
  }, [bookingId, currentBillId]);

  const earlier = revisions.filter((r) => r.id !== currentBillId);
  if (!earlier.length) return null;

  const open = async (revision, how) => {
    setError("");
    try {
      const data = mapBillPrint(await billApi.print(bookingId, revision));
      const html = billHtml(data);
      if (how === "print") printHtml(html, `Bill ${data.bill.billNumber}`);
      else if (!viewHtml(html)) setError("The browser blocked the new tab — allow pop-ups for this site, or use Print.");
    } catch (e) {
      setError(e.message || "Could not open that bill.");
    }
  };

  return (
    <Segment>
      <strong><Icon name="history" />Earlier versions of this bill</strong>
      {error && <Message negative size="small" content={error} />}
      <Table compact unstackable size="small">
        <Table.Body>
          {[...earlier].reverse().map((r) => (
            <Table.Row key={r.id}>
              <Table.Cell>
                <strong>{r.billNumber}</strong> <Label size="mini">{STATUS_TEXT[r.status]}</Label>
                <div style={{ fontSize: "0.85em", opacity: 0.7 }}>
                  {r.status === "CANCELLED"
                    ? `Cancelled ${fmtDateTime(r.cancelledAt)}${r.cancelReason ? ` — ${r.cancelReason}` : ""}`
                    : `Made ${fmtDateTime(r.createdAt)}`}
                </div>
              </Table.Cell>
              <Table.Cell textAlign="right" style={{ whiteSpace: "nowrap" }}>{formatRupees(r.grandTotal)}</Table.Cell>
              <Table.Cell collapsing>
                <Button size="mini" basic onClick={() => open(r.revision, "view")}><Icon name="eye" />View</Button>
                <Button size="mini" basic onClick={() => open(r.revision, "print")}><Icon name="print" />Print</Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );
}
